import { useQuery } from '@tanstack/react-query';
import { Archive, Download, Paintbrush, RotateCcw, Undo2 } from 'lucide-react';
import api from '@/lib/api';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import KitProgress from './KitProgress';
import type { BrandKitDetail, BrandKitSummary } from './types';

const STATUS_BADGE: Record<BrandKitSummary['status'], { label: string; variant: 'gray' | 'blue' | 'green' | 'red' | 'yellow' }> = {
  draft: { label: 'Draft', variant: 'gray' },
  packaging: { label: 'Generating package…', variant: 'blue' },
  ready: { label: 'Ready', variant: 'green' },
  failed: { label: 'Failed', variant: 'red' },
  archived: { label: 'Archived', variant: 'gray' },
};

interface KitCardProps {
  kit: BrandKitSummary;
  busy: boolean;
  onResumeStudio: (kitId: string) => void;
  onDownload: (kitId: string) => void;
  onApply: (kitId: string) => void;
  onRevert: (kitId: string) => void;
  onRetry: (kitId: string) => void;
  onArchive: (kitId: string) => void;
}

export default function KitCard({
  kit,
  busy,
  onResumeStudio,
  onDownload,
  onApply,
  onRevert,
  onRetry,
  onArchive,
}: KitCardProps) {
  // While the package job runs, the card polls its own detail for the
  // 12-step progress rail (SiteWizardProgress cadence).
  const { data: detail } = useQuery<BrandKitDetail>({
    queryKey: ['branding', 'kit', kit.id],
    queryFn: () => api.get<BrandKitDetail>(`/admin/branding/kits/${kit.id}`),
    enabled: kit.status === 'packaging' || kit.status === 'failed',
    refetchInterval: kit.status === 'packaging' ? 2000 : false,
  });

  const badge = STATUS_BADGE[kit.status];

  return (
    <div className="rounded-xl border border-line bg-surface p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-medium text-ink">{kit.name}</h3>
        <Badge variant={badge.variant}>{badge.label}</Badge>
        {kit.applied_at && <Badge variant="green">Applied to storefront</Badge>}
        <span className="ml-auto text-xs text-ink-muted">
          {new Date(kit.created_at).toLocaleDateString()}
        </span>
      </div>

      {/* Brand preview: the kit's own lockup + palette, so the library shows
          the brand instead of a text row (2026-08-14 polish finding). */}
      {kit.preview && (kit.preview.headerLogoUrl || kit.preview.primaryColor) && (
        <div className="mt-3 flex items-center gap-4 rounded-lg border border-line bg-surface-muted/40 p-3">
          {kit.preview.headerLogoUrl && (
            <img
              src={kit.preview.headerLogoUrl}
              alt={`${kit.name} logo`}
              className="h-10 w-auto max-w-[45%] object-contain"
              loading="lazy"
            />
          )}
          <div className="flex items-center gap-2">
            {[kit.preview.primaryColor, kit.preview.accentColor]
              .filter((hex): hex is string => !!hex)
              .map((hex) => (
                <span
                  key={hex}
                  className="h-6 w-6 rounded-full border border-line shadow-sm"
                  style={{ backgroundColor: hex }}
                  title={hex}
                />
              ))}
            {kit.preview.heading && (
              <span className="ml-1 text-xs text-ink-muted">{kit.preview.heading}</span>
            )}
          </div>
        </div>
      )}

      {kit.status === 'packaging' && detail && (
        <div className="mt-3">
          <KitProgress progress={detail.progress} />
        </div>
      )}
      {kit.status === 'failed' && (
        <p className="mt-2 text-sm text-red-600">{detail?.kit.error ?? 'Package generation failed.'}</p>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {kit.status === 'draft' && (
          <Button size="sm" onClick={() => onResumeStudio(kit.id)} disabled={busy}>
            <Paintbrush className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Open studio
          </Button>
        )}
        {kit.status === 'ready' && (
          <>
            {kit.has_package && (
              <Button size="sm" variant="outline" onClick={() => onDownload(kit.id)} disabled={busy}>
                <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Download kit
              </Button>
            )}
            {kit.applied_at ? (
              <Button size="sm" variant="outline" onClick={() => onRevert(kit.id)} disabled={busy}>
                <Undo2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Revert storefront
              </Button>
            ) : (
              <Button size="sm" onClick={() => onApply(kit.id)} disabled={busy}>
                Apply to storefront
              </Button>
            )}
          </>
        )}
        {kit.status === 'failed' && (
          <Button size="sm" variant="outline" onClick={() => onRetry(kit.id)} disabled={busy}>
            <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Retry
          </Button>
        )}
        {kit.status !== 'packaging' && (
          <Button size="sm" variant="ghost" onClick={() => onArchive(kit.id)} disabled={busy}>
            <Archive className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Archive
          </Button>
        )}
      </div>
    </div>
  );
}
